import { gql } from '@apollo/client';

/**
 * GraphQL contract for the procedure-cost dialog. The shape mirrors
 * `procedureCostMock.ProcedureCostDetails` so the dialog can use the same
 * types whether data is mocked (storybook) or live (this query).
 *
 * Backend resolver lives at view-api: `StaffAttributionCost.scala`.
 *  - `costCents` / `hourlyRate` are null when the org has
 *    `showStaffCost = false` or the staff member is missing `hourlyRate`
 *    (per ADR-047: cost is resolver-computed, not stored).
 *  - `segmentCount > 1` indicates the staff has multiple presence
 *    intervals within this procedure (e.g., left and re-entered the OR).
 *    The edit mutation splits proportionally across those segments.
 */
export const PROCEDURE_COST_QUERY = gql`
  query ProcedureCost($procedureId: Long!) {
    procedureCost(procedureId: $procedureId) {
      procedureId
      procedureType
      surgeonName
      patientName
      patientAge
      patientSex
      orRoomName
      date
      scheduledStartText
      scheduledEndText
      scheduledDurationSeconds
      orStartText
      orEndText
      orDurationSeconds
      staff {
        staffId
        name
        role
        hourlyRate
        presence
        originalDurationSeconds
        effectiveDurationSeconds
        costCents
        isManuallyEdited
        segmentCount
      }
    }
  }
`;

export const EDIT_STAFF_ATTRIBUTION_SECONDS = gql`
  mutation EditStaffAttributionSeconds($procedureId: Long!, $staffId: Long!, $newSeconds: Int!) {
    editStaffAttributionSeconds(procedureId: $procedureId, staffId: $staffId, newSeconds: $newSeconds) {
      procedureId
      staff {
        staffId
        name
        role
        hourlyRate
        presence
        originalDurationSeconds
        effectiveDurationSeconds
        costCents
        isManuallyEdited
        segmentCount
      }
    }
  }
`;

export const REVERT_STAFF_ATTRIBUTION = gql`
  mutation RevertStaffAttribution($procedureId: Long!, $staffId: Long!) {
    revertStaffAttribution(procedureId: $procedureId, staffId: $staffId) {
      procedureId
      staff {
        staffId
        name
        role
        hourlyRate
        presence
        originalDurationSeconds
        effectiveDurationSeconds
        costCents
        isManuallyEdited
        segmentCount
      }
    }
  }
`;

export interface ProcedureCostStaffPayload {
  staffId: string;
  name: string;
  role: string;
  hourlyRate: number | null;
  presence: string;
  originalDurationSeconds: number;
  effectiveDurationSeconds: number;
  costCents: number | null;
  isManuallyEdited: boolean;
  segmentCount: number;
}

export interface ProcedureCostPayload {
  procedureId: string;
  procedureType: string;
  surgeonName: string;
  patientName: string;
  patientAge: number | null;
  patientSex: string | null;
  orRoomName: string;
  date: string;
  scheduledStartText: string;
  scheduledEndText: string;
  scheduledDurationSeconds: number;
  orStartText: string;
  orEndText: string;
  orDurationSeconds: number;
  staff: ProcedureCostStaffPayload[];
}

export interface ProcedureCostQueryData {
  procedureCost: ProcedureCostPayload;
}

export interface EditMutationData {
  editStaffAttributionSeconds: ProcedureCostPayload;
}

export interface RevertMutationData {
  revertStaffAttribution: ProcedureCostPayload;
}
